import Table from "@/components/Table/Table";
import TableSkeleton from "@/components/Table/TableSkeleton";
import { useDebounce } from "@/hooks/debounce";
import { useAppDispatch } from "@/store/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TextField } from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { ViewTerritoryReport } from "@/store/slices/territorySlice";

const ViewTerritory = () => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce(searchText);
    const dispatch = useAppDispatch();
    const routeParams = useParams();
    const { isFetching, viewReport } = useSelector((state: RootState) => state.territory)


    const queryParams = useMemo(
        () => ({
            Id: routeParams?.fileId as string,
            from: (page - 1) * rowsPerPage + 1,
            text: debouncedSearch,
            to: page * rowsPerPage,
        }),
        [page, rowsPerPage, debouncedSearch],
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleFetchTerritoryReport = async () => {
        if (!queryParams?.Id) return;
        dispatch(ViewTerritoryReport(queryParams))

    };

    const columns = [
        {
            name: "TerritoryName",
            selector: (row: any) => row?.TerritoryName,
            width: "120px",
        },
        {
            name: "ParentTerritoryName",
            selector: (row: any) => row?.ParentTerritoryName,
            width: "120px",
        },
        {
            name: "Status",
            selector: (row: any) => row?.Status,
            width: "105px",
        },
        {
            name: "Message",
            selector: (row: any) =>
                row?.Message ? <span>{row?.Message}</span> : "-",
            width: "105px",
        },
    ];

    const skeletonColumns = columns.map((col) => {
        return { name: col.name, width: col.width };
    });

    useEffect(() => {
        handleFetchTerritoryReport();
    }, [queryParams, routeParams]);

    return (
        <section className="bg-white pt-[16px] mx-[16px] shadow-[0_0_13px_#523f690d]">
            <div className="border-b border-[#ebedf2] px-[25px] pl-[15px] min-h-[60px] rounded-tl rounded-tr flex gap-[5px] justify-between items-center">
                <h5 className="text-[16px] text-[#646c9a] font-medium">
                    View Territories
                </h5>
                <TextField
                    id="search"
                    label="Search"
                    variant="standard"
                    onChange={handleSearchChange}
                    className="mr-[10px] w-[250px]"
                    sx={{
                        "& .MuiInputLabel-root": {
                            marginTop: "-10px",
                        },
                        "& .MuiInput-root": {
                            height: "16px",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                            marginTop: "0px",
                        },
                    }}
                />
            </div>
            <div className="pt-[5px] px-[10px]">
                {isFetching ? (
                    <TableSkeleton columns={skeletonColumns} />
                ) : (
                    <Table
                        columns={columns}
                        data={viewReport.Items}
                        paginationProps={{
                            isPagination: true,
                            totalCount: viewReport.TotalRecords,
                            pageSelected: page,
                            setPageSelected: setPage,
                            rowsPerPageValue: rowsPerPage,
                            setRowsPerPageValue: setRowsPerPage,
                        }}
                        paginationLabel={viewReport?.TotalRecords > 0 ? "reports" : "report"}
                    />
                )}
            </div>
        </section>
    );
};

export default ViewTerritory;
